import { Navigate, useLocation } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { useAuth } from '~/entities/session/model/use-auth'
import { DASHBOARD_REDIRECT_ROUTES } from '~/shared/lib/auth-redirect-routes'

/**
 * Sends an authenticated business user to /dashboard, but only on the routes in
 * DASHBOARD_REDIRECT_ROUTES (entry/landing pages). Every other route - including
 * recovery/invite links - renders normally, so a logged-in user is never bounced
 * off a sub-page. Replaces the ad-hoc per-route "if logged in, go to dashboard".
 */
export function AuthedDashboardRedirect({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  const { isAuthenticated, isLoading } = useAuth()

  const normalizedPath =
    pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname

  if (!DASHBOARD_REDIRECT_ROUTES.includes(normalizedPath)) {
    return <>{children}</>
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="inline-block border-4 border-current border-r-transparent border-solid rounded-full w-8 h-8 animate-spin motion-reduce:animate-[spin_1.5s_linear_infinite] align-[-0.125em]" />
      </div>
    )
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" />
  }

  return <>{children}</>
}
